import * as yup from 'yup';
import { useMemo } from 'react';
import {
  CurrencyConfig,
  FormQuestion,
  NominationForm,
} from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import { TFunction, useTranslation } from 'react-i18next';
import { emailRegex } from '@percent/grants-nomination-form/utils/regex';
import { currencyCodes } from '@percent/utility';
import {
  DEFAULT_OPERATING_BUDGET_CURRENCY,
  MAX_AMOUNT_MINOR_UNITS,
} from '@percent/grants-nomination-form/constants/operatingBudget';

type Schema = Record<string, yup.AnySchema>;

const ORGANISATION_CONTACT_EMAIL_KEY = 'nomination.organisationContact.email';

const MAX_TEXT_FIELD_LENGTH = 255;
const MAX_TEXTAREA_FIELD_LENGTH = 5000;
const LENIENT_URL_EXPRESSION = /^[^@\s]+\.[^@\s]+$/;

/**
 * Currency half of a money field: an ISO code narrowed to the question's allow-list when it carries one, and
 * seeded with the question's default currency when left blank.
 */
const buildCurrencySchema = (t: TFunction, config?: CurrencyConfig) => {
  const allowed = config?.allowedCurrencies?.length ? config.allowedCurrencies : [...currencyCodes];
  const fallback = config?.defaultCurrency ?? DEFAULT_OPERATING_BUDGET_CURRENCY;

  return yup
    .string()
    .transform((v) => (v === undefined || v === '' ? fallback : v))
    .oneOf(allowed, t('validation.invalidCurrency'))
    .default(fallback);
};

export const createBaseValidation = (question: FormQuestion, t: TFunction): yup.AnySchema => {
  if (question.type === 'number') {
    const numberSchema = yup.number().typeError(t('validation.invalidNumber'));

    return question.required ? numberSchema.required(t('validation.required')) : numberSchema;
  }

  if (question.type === 'money') {
    const moneySchema = yup
      .number()
      .typeError(t('validation.invalidMoney'))
      .integer(t('validation.invalidMoney'))
      .positive(t('validation.invalidMoney'))
      .max(MAX_AMOUNT_MINOR_UNITS, t('validation.maxAmount'));

    return question.amountRequired ? moneySchema.required(t('validation.required')) : moneySchema;
  }

  if (question.type === 'currency') {
    return buildCurrencySchema(t, question);
  }

  const baseSchema = question.required
    ? yup.string().trim(t('validation.required')).required(t('validation.required'))
    : yup.string().trim();

  switch (question.type) {
    case 'text':
      return baseSchema.max(MAX_TEXT_FIELD_LENGTH, t('validation.maxLength', { maxLength: MAX_TEXT_FIELD_LENGTH }));
    case 'textarea': {
      const maxLength = 'maxLength' in question && question.maxLength ? question.maxLength : MAX_TEXTAREA_FIELD_LENGTH;
      return baseSchema.max(maxLength, t('validation.maxLength', { maxLength }));
    }
    case 'email':
      return baseSchema.test(
        'email',
        t('validation.email'),
        (value) => (!question.required && value === '') || emailRegex.test(value ?? ''),
      );
    case 'website':
      return baseSchema
        .matches(LENIENT_URL_EXPRESSION, t('validation.website'))
        .max(MAX_TEXT_FIELD_LENGTH, t('validation.maxLength', { maxLength: MAX_TEXT_FIELD_LENGTH }));
    case 'select': {
      if (question.multiple) {
        const validOptions = question.options?.map((opt) => opt.value) || [];
        const baseArray = yup.array().of(yup.string().oneOf(validOptions, t('validation.select')));

        return question.required
          ? baseArray.required(t('validation.required')).min(1, t('validation.required'))
          : baseArray;
      }

      return baseSchema.oneOf(question.options?.map((opt) => opt.value) || [], t('validation.select'));
    }
    default:
      return baseSchema;
  }
};

type FormTestContext = yup.TestContext & { from?: { value: { nominator?: { email?: string } } }[] };

const differentFromNominatorEmail = (t: TFunction) =>
  yup
    .string()
    .test('different-from-nominator', t('validation.emailSameAsNominator'), function (this: FormTestContext, value) {
      const nominatorEmail = this.from?.[1]?.value?.nominator?.email;

      return !value || !nominatorEmail || value.trim().toLowerCase() !== nominatorEmail.trim().toLowerCase();
    });

const createNestedSchema = (obj: Schema): Schema => {
  const result: Schema = {};

  for (const [key, value] of Object.entries(obj)) {
    result[key] = yup.isSchema(value) ? value : yup.object().shape(createNestedSchema(value));
  }

  return result;
};

const assignKey = (schema: Schema, key: string, rule: yup.AnySchema, arrayFieldsKeys: Set<string>) => {
  const keyParts = key.replace('[]', '').split('.');
  if (key.includes('[]')) {
    arrayFieldsKeys.add(key);
  }

  let currentLevel = schema;
  keyParts.forEach((part, index) => {
    if (index === keyParts.length - 1) {
      currentLevel[part] = rule;
    } else {
      currentLevel[part] = currentLevel[part] || {};
      currentLevel = currentLevel[part] as unknown as Schema;
    }
  });
};

export const useValidationSchema = (formData?: NominationForm) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const schema: Schema = {};
    const arrayFieldsKeys = new Set<string>();

    formData?.pages.forEach((page) => {
      page.questions?.forEach((question) => {
        const baseRule = createBaseValidation(question, t);

        let validationRule: yup.AnySchema = baseRule;
        if (question.key === ORGANISATION_CONTACT_EMAIL_KEY) {
          validationRule = (baseRule as yup.StringSchema).concat(differentFromNominatorEmail(t));
        }

        assignKey(schema, question.key, validationRule, arrayFieldsKeys);

        if (question.type === 'money' && question.currencyKey) {
          assignKey(schema, question.currencyKey, buildCurrencySchema(t, question), arrayFieldsKeys);
        }
      });
    });

    return {
      schema: yup.object().shape(createNestedSchema(schema)),
      arrayFieldsKeys,
    };
  }, [formData, t]);
};
